import { useCallback } from "react";
import JSZip from "jszip";
import type { BuildingStructureBox } from "@/utils/supabase";
import type { TenantInstance } from "@/three/config/types";

interface ZipDownloadProps {
  buildingId: string;
  buildingTitle?: string | null;
  buildingStructure?: BuildingStructureBox[] | null;
  userColor?: string | null;
  tenants: TenantInstance[];
}

/**
 * 거주 기간 계산 (한국어)
 */
function formatResidencePeriod(createdAt: string): string {
  const created = new Date(createdAt);
  const now = new Date();

  const diffMs = now.getTime() - created.getTime();
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

  if (diffDays < 1) return "오늘 입주 🆕";
  if (diffDays < 7) return `${diffDays}일째 거주 중 🏡`;
  if (diffDays < 30) return `${Math.floor(diffDays / 7)}주째 거주 중 🏡`;

  const diffMonths = Math.floor(diffDays / 30);
  if (diffMonths < 12) return `${diffMonths}개월째 거주 중 🏡`;

  const years = Math.floor(diffMonths / 12);
  const remainingMonths = diffMonths % 12;
  if (remainingMonths === 0) return `${years}년째 거주 중 🏡`;
  return `${years}년 ${remainingMonths}개월째 거주 중 🏡`;
}

/**
 * 입주일 포맷
 */
function formatMoveInDate(createdAt: string): string {
  const date = new Date(createdAt);
  return `${date.getFullYear()}년 ${date.getMonth() + 1}월`;
}

/**
 * 입주자 명단 txt 생성
 */
function generateTenantListTxt(
  buildingTitle: string,
  tenants: TenantInstance[]
): string {
  const today = new Date();
  const dateStr = `${today.getFullYear()}년 ${
    today.getMonth() + 1
  }월 ${today.getDate()}일`;

  let txt = "";
  txt += "╔═══════════════════════════════════════════════════════╗\n";
  txt += `║   🏠 ${buildingTitle || "이름없는 건물"} 입주자 명단\n`;
  txt += `║      발급일: ${dateStr}\n`;
  txt += "╚═══════════════════════════════════════════════════════╝\n\n";

  if (tenants.length === 0) {
    txt += "┌─────────────────────────────────────────────┐\n";
    txt += "│  아직 입주자가 없습니다. 👻                │\n";
    txt += "└─────────────────────────────────────────────┘\n";
  } else {
    tenants.forEach((tenant, index) => {
      const isLast = index === tenants.length - 1;
      const createdAt = (tenant as any).createdAt || new Date().toISOString();

      txt += "┌─────────────────────────────────────────────┐\n";
      txt += `│  💬 "${tenant.text}"\n`;
      txt += `│  ├─ 입주자: User ${tenant.userId.slice(
        0,
        4
      )}...${tenant.userId.slice(-4)}\n`;
      txt += `│  ├─ 입주일: ${formatMoveInDate(createdAt)}\n`;
      txt += `│  └─ 거주기간: ${formatResidencePeriod(createdAt)}\n`;
      txt += isLast
        ? "└─────────────────────────────────────────────┘\n"
        : "├─────────────────────────────────────────────┤\n";
    });
  }

  return txt;
}

/**
 * Hex 색상을 RGB로 변환하고 파스텔톤 처리
 */
function colorToPastelRGB(hex: string, amount: number): string {
  // Hex to RGB
  let r = 255,
    g = 255,
    b = 255;
  if (/^#([A-Fa-f0-9]{3}){1,2}$/.test(hex)) {
    let c = hex.substring(1).split("");
    if (c.length === 3) {
      c = [c[0], c[0], c[1], c[1], c[2], c[2]];
    }
    const val = parseInt(c.join(""), 16);
    r = (val >> 16) & 255;
    g = (val >> 8) & 255;
    b = val & 255;
  }

  // toPastel logic: mix with white (255)
  r = Math.floor(r + (255 - r) * amount);
  g = Math.floor(g + (255 - g) * amount);
  b = Math.floor(b + (255 - b) * amount);

  return `rgb(${r}, ${g}, ${b})`;
}

/**
 * 건물 구조를 2D 아이소메트릭으로 렌더링
 */
function renderBuildingToCanvas(
  ctx: CanvasRenderingContext2D,
  structure: BuildingStructureBox[],
  offsetX: number,
  offsetY: number,
  userColor: string,
  scale: number = 15
): void {
  if (!structure || structure.length === 0) return;

  // 셰이더와 최대한 유사하게 색상 계산
  const vibrantColor = colorToPastelRGB(userColor, 0.5);

  const parseRGB = (rgbStr: string) => {
    const match = rgbStr.match(/\d+/g);
    return match ? match.map(Number) : [255, 255, 255];
  };

  const mixColor = (baseRGB: number[], factor: number[]) => {
    return `rgb(${Math.floor(baseRGB[0] * factor[0])}, ${Math.floor(
      baseRGB[1] * factor[1]
    )}, ${Math.floor(baseRGB[2] * factor[2])})`;
  };

  const baseRGB = parseRGB(vibrantColor);
  const roofColor = vibrantColor;
  const zPlusColor = mixColor(baseRGB, [1.0, 0.98, 0.96]);
  const xPlusColor = mixColor(baseRGB, [0.95, 0.95, 1.0]);

  // 아이소메트릭 변환
  const toIso = (x: number, y: number, z: number): [number, number] => {
    const isoX = (x - z) * scale * 0.866;
    const isoY = (x + z) * scale * 0.5 - y * scale;
    return [offsetX + isoX, offsetY + isoY];
  };

  // 정렬 (뒤쪽 박스부터 그리기: X+Z가 작을수록 뒤쪽)
  const sortedBoxes = [...structure].sort((a, b) => {
    const depthA = a.position[0] + a.position[2];
    const depthB = b.position[0] + b.position[2];
    return depthA - depthB;
  });

  sortedBoxes.forEach((box) => {
    const [px, py, pz] = box.position;
    const [sx, sy, sz] = box.scale;
    const halfX = sx / 2;
    const halfY = sy / 2;
    const halfZ = sz / 2;

    // 8개의 꼭짓점
    const vertices = [
      toIso(px - halfX, py - halfY, pz - halfZ), // 0
      toIso(px + halfX, py - halfY, pz - halfZ), // 1
      toIso(px + halfX, py - halfY, pz + halfZ), // 2
      toIso(px - halfX, py - halfY, pz + halfZ), // 3
      toIso(px - halfX, py + halfY, pz - halfZ), // 4
      toIso(px + halfX, py + halfY, pz - halfZ), // 5
      toIso(px + halfX, py + halfY, pz + halfZ), // 6
      toIso(px - halfX, py + halfY, pz + halfZ), // 7
    ];

    // 윗면 (Y+)
    ctx.fillStyle = roofColor;
    ctx.beginPath();
    ctx.moveTo(vertices[4][0], vertices[4][1]);
    ctx.lineTo(vertices[5][0], vertices[5][1]);
    ctx.lineTo(vertices[6][0], vertices[6][1]);
    ctx.lineTo(vertices[7][0], vertices[7][1]);
    ctx.closePath();
    ctx.fill();

    const drawWindows = (
      v0: [number, number],
      v1: [number, number],
      v2: [number, number],
      v3: [number, number],
      fWidth: number,
      fHeight: number,
      wallColor: string
    ) => {
      const winSize = 0.35;
      const winGap = 0.2;
      const cellSize = winSize + winGap;
      const cols = Math.floor(fWidth / cellSize);
      const rows = Math.floor(fHeight / cellSize);

      for (let iy = 0; iy < rows; iy++) {
        for (let ix = 0; ix < cols; ix++) {
          const u = ((ix + 0.5) * cellSize) / fWidth;
          const v = ((iy + 0.5) * cellSize) / fHeight;

          // 선형 보간으로 창문 중심점 계산
          const x =
            v0[0] * (1 - u) * (1 - v) +
            v1[0] * u * (1 - v) +
            v2[0] * u * v +
            v3[0] * (1 - u) * v;
          const y =
            v0[1] * (1 - u) * (1 - v) +
            v1[1] * u * (1 - v) +
            v2[1] * u * v +
            v3[1] * (1 - u) * v;

          // 불 켜진 창문 랜덤 (셰이더와 비슷하게)
          const isLit = Math.random() > 0.7;
          ctx.fillStyle = isLit ? "#ffebba" : "rgba(0, 0, 0, 0.2)";

          const w = (scale * winSize) / 2;
          ctx.beginPath();
          ctx.moveTo(x, y - w);
          ctx.lineTo(x + w * 0.866, y);
          ctx.lineTo(x, y + w);
          ctx.lineTo(x - w * 0.866, y);
          ctx.closePath();
          ctx.fill();
        }
      }
    };

    // 앞면 (Z+)
    ctx.fillStyle = zPlusColor;
    ctx.beginPath();
    ctx.moveTo(vertices[3][0], vertices[3][1]);
    ctx.lineTo(vertices[2][0], vertices[2][1]);
    ctx.lineTo(vertices[6][0], vertices[6][1]);
    ctx.lineTo(vertices[7][0], vertices[7][1]);
    ctx.closePath();
    ctx.fill();
    drawWindows(
      vertices[3],
      vertices[2],
      vertices[6],
      vertices[7],
      sx,
      sy,
      zPlusColor
    );

    // 옆면 (X+)
    ctx.fillStyle = xPlusColor;
    ctx.beginPath();
    ctx.moveTo(vertices[1][0], vertices[1][1]);
    ctx.lineTo(vertices[2][0], vertices[2][1]);
    ctx.lineTo(vertices[6][0], vertices[6][1]);
    ctx.lineTo(vertices[5][0], vertices[5][1]);
    ctx.closePath();
    ctx.fill();
    drawWindows(
      vertices[1],
      vertices[2],
      vertices[6],
      vertices[5],
      sz,
      sy,
      xPlusColor
    );

    // 외곽선
    ctx.strokeStyle = "rgba(0, 0, 0, 0.05)";
    ctx.lineWidth = 0.5;
    ctx.stroke();
  });
}

/**
 * 인증서 이미지 생성
 */
async function generateCertificateImage(
  buildingTitle: string,
  structure: BuildingStructureBox[],
  userColor: string
): Promise<Blob> {
  // 1. 템플릿 이미지 로드
  const backgroundImg = new Image();
  backgroundImg.src = import.meta.env.BASE_URL + "/images/certificate.png";

  await new Promise((resolve, reject) => {
    backgroundImg.onload = resolve;
    backgroundImg.onerror = reject;
  });

  const width = backgroundImg.width;
  const height = backgroundImg.height;

  const canvas = document.createElement("canvas");
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext("2d")!;

  // 배경 이미지 그리기
  ctx.drawImage(backgroundImg, 0, 0, width, height);

  // 2. 건물명 (따옴표 없이)
  ctx.fillStyle = "#ffffff";
  ctx.font = "600 26px 'Pretendard GOV Variable'";
  ctx.textAlign = "center";
  ctx.fillText(`${buildingTitle || "이름없는 건물"}`, width / 2, height * 0.2);

  // 3. 건물 렌더링
  renderBuildingToCanvas(
    ctx,
    structure,
    width / 2,
    height * 0.52,
    userColor,
    width * 0.045
  );

  return new Promise((resolve) => {
    canvas.toBlob((blob) => resolve(blob!), "image/png");
  });
}

/**
 * Zip 다운로드 훅
 */
export function useZipDownload() {
  const downloadZip = useCallback(
    async ({
      buildingId,
      buildingTitle,
      buildingStructure,
      userColor,
      tenants,
    }: ZipDownloadProps): Promise<boolean> => {
      try {
        const zip = new JSZip();

        // 1. 인증서 이미지 생성
        const certificateBlob = await generateCertificateImage(
          buildingTitle || "이름없는 건물",
          buildingStructure || [],
          userColor || "#ffffff"
        );
        zip.file("certificate.png", certificateBlob);

        // 2. 입주자 명단 txt 생성
        const tenantTxt = generateTenantListTxt(
          buildingTitle || "이름없는 건물",
          tenants
        );
        zip.file("tenants.txt", tenantTxt);

        // 3. Zip 생성 및 다운로드
        const content = await zip.generateAsync({ type: "blob" });

        const url = URL.createObjectURL(content);
        const a = document.createElement("a");
        a.href = url;
        a.download = `${buildingTitle || "building"}.zip`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);

        return true;
      } catch (error) {
        console.error("Zip 다운로드 실패:", error);
        return false;
      }
    },
    []
  );

  return { downloadZip };
}
